import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Typography,
  Box,
  Tooltip,
  ListItemButton,
  Collapse,
  Popper,
  Paper,
  ClickAwayListener,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
// Navigation 3.1: Lucide-Icons — eine Strichstärke, ruhige Geometrie
import {
  Home, MessageCircle, Users, Target, Inbox, BookUser, Building2,
  Briefcase, Package, CalendarDays, Clock, Megaphone, UserRound,
  Folder, ChevronDown, ChevronUp, ChevronRight,
} from 'lucide-react';
import GlobalSearch from './GlobalSearch';
import ThemeModeToggle from './ThemeModeToggle';
import AssistantPanel from './AssistantPanel';
import KinAvatar from './KinAvatar';

const drawerWidth = 232;
const railWidth = 76;

// Einheitliche Icon-Sprache (Lucide): 20 px, 1.75er-Strich, Farbe erbt
const navIcon = (Icon) => <Icon size={20} strokeWidth={1.75} />;

// Hauptnavigation: nach Aufgaben gruppiert, Häufiges oben
const navigationItems = [
  { title: 'Startseite', icon: navIcon(Home), path: '/' },
  { title: 'Interaktionen', icon: navIcon(MessageCircle), path: '/interactions' },
  {
    title: 'CRM',
    type: 'menu',
    icon: navIcon(Users),
    children: [
      { title: 'Leads', icon: navIcon(Target), path: '/leads' },
      { title: 'Agenturanfragen', icon: navIcon(Inbox), path: '/agency-submissions' },
      { title: 'Kontakte', icon: navIcon(BookUser), path: '/contacts' },
      { title: 'Unternehmen', icon: navIcon(Building2), path: '/accounts' },
    ],
  },
  {
    title: 'Projekte & Zeit',
    type: 'menu',
    icon: navIcon(Briefcase),
    children: [
      { title: 'Projekte', icon: navIcon(Package), path: '/projects' },
      { title: 'Teamkalender', icon: navIcon(CalendarDays), path: '/team-calendar' },
      { title: 'Zeiterfassung', icon: navIcon(Clock), path: '/timetracker' },
    ],
  },
  { title: 'Marketing', icon: navIcon(Megaphone), path: '/marketing' },
];

// Selten genutzt → optisch abgesetzt unten
const adminItems = [
  { title: 'Benutzer', icon: navIcon(UserRound), path: '/users' },
  { title: 'Dateien', icon: navIcon(Folder), path: '/files' },
];

function stringToColor(string) {
  if (!string) return '#000000';
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
}

// Initialen lesbar auf jeder Hash-Farbe: Tinte auf hellen, Weiß auf dunklen
// Hintergründen (Luminanz-Regel wie im StatusDonut) — MUIs Default nimmt
// background.default und wird im Dark Mode zu Schwarz-auf-Dunkel
function avatarTextColor(hex) {
  if (!hex.startsWith('#') || hex.length < 7) return '#FFFFFF';
  const lum = 0.299 * parseInt(hex.slice(1, 3), 16)
    + 0.587 * parseInt(hex.slice(3, 5), 16)
    + 0.114 * parseInt(hex.slice(5, 7), 16);
  return lum > 150 ? '#14181F' : '#FFFFFF';
}

// aktiver Zustand: exakt für "/", sonst Präfix (deckt Detailseiten ab)
function pathIsActive(pathname, path) {
  if (path === '/') return pathname === '/';
  return pathname === path || pathname.startsWith(path + '/');
}

// Wortmarke „Kinflow" (zweifarbig) mit Teal-Punkt als Mark.
// variant="sidebar": helle Fassung für die dunkle Leiste (V1);
// compact: nur der Punkt (Rail-Modus).
function Wordmark({ onClick, variant, compact }) {
  const aufDunkel = variant === 'sidebar';
  if (compact) {
    return (
      <Box onClick={onClick} sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4EC8C0', cursor: 'pointer' }} />
    );
  }
  return (
    <Box onClick={onClick} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none' }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: aufDunkel ? '#4EC8C0' : 'secondary.main', flex: 'none' }} />
      <Typography component="span" sx={{ fontWeight: 800, fontSize: '1.3rem', letterSpacing: '-0.02em', lineHeight: 1, color: aufDunkel ? '#E9ECF2' : 'text.primary' }}>
        Kin<Box component="span" sx={{ color: aufDunkel ? '#A5B4FC' : 'primary.main' }}>flow</Box>
      </Typography>
    </Box>
  );
}

const Layout = ({ children, user, onLogout }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [open, setOpen] = useState(true);          // Desktop: voll / Rail
  const [mobileOpen, setMobileOpen] = useState(false); // Mobile: Overlay
  const [assistantOpen, setAssistantOpen] = useState(false); // Kin-Panel

  // Auto-Refresh: hat Kin eine Änderung angewendet, wird die aktuelle
  // Seite neu gemountet (lädt ihre Daten neu) — das Panel bleibt stehen.
  const [dataTick, setDataTick] = useState(0);
  useEffect(() => {
    const handler = () => setDataTick((t) => t + 1);
    window.addEventListener('kin-applied', handler);
    return () => window.removeEventListener('kin-applied', handler);
  }, []);
  const [openMenus, setOpenMenus] = useState({});
  const [flyoutMenu, setFlyoutMenu] = useState(null);
  const closeTimeoutRef = React.useRef(null);

  const expanded = isMobile ? true : open; // Beschriftungen sichtbar?

  const avatarBg = user ? stringToColor(`${user.first_name} ${user.last_name}`) : '#000000';

  // Gruppe mit aktivem Kind automatisch aufklappen
  useEffect(() => {
    const next = {};
    navigationItems.forEach((item) => {
      if (item.type === 'menu' && item.children.some((c) => pathIsActive(pathname, c.path))) {
        next[item.title] = true;
      }
    });
    setOpenMenus((prev) => ({ ...prev, ...next }));
  }, [pathname]);

  const handleDrawerToggle = () => {
    if (isMobile) setMobileOpen((v) => !v);
    else setOpen((v) => !v);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setFlyoutMenu(null);
    if (isMobile) setMobileOpen(false);
  };

  const handleMenuClick = (title) => {
    setOpenMenus((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const handleFlyoutEnter = (event, item) => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setFlyoutMenu({ title: item.title, children: item.children, anchorEl: event.currentTarget });
  };
  const handleFlyoutLeave = () => {
    closeTimeoutRef.current = setTimeout(() => setFlyoutMenu(null), 150);
  };
  const handleFlyoutPaperEnter = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
  };

  const renderSingle = (item, key) => {
    const active = pathIsActive(pathname, item.path);
    return (
      <ListItem key={key} disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          selected={active}
          onClick={() => handleNavigation(item.path)}
          sx={{ minHeight: 42, px: 2, justifyContent: expanded ? 'initial' : 'center' }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: expanded ? 2 : 'auto', justifyContent: 'center', color: undefined }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText primary={item.title} sx={{ opacity: expanded ? 1 : 0, display: expanded ? 'block' : 'none' }} />
        </ListItemButton>
      </ListItem>
    );
  };

  const renderMenu = (item, key) => {
    const isMenuOpen = !!openMenus[item.title];
    const groupActive = item.children.some((c) => pathIsActive(pathname, c.path));
    return (
      <Box
        key={key}
        onMouseEnter={!expanded ? (e) => handleFlyoutEnter(e, item) : undefined}
        onMouseLeave={!expanded ? handleFlyoutLeave : undefined}
      >
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            selected={groupActive && !isMenuOpen}
            onClick={expanded ? () => handleMenuClick(item.title) : undefined}
            sx={{ minHeight: 42, px: 2, position: 'relative', justifyContent: expanded ? 'initial' : 'center' }}
          >
            <ListItemIcon sx={{ minWidth: 0, mr: expanded ? 2 : 'auto', justifyContent: 'center', color: undefined }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.title} sx={{ opacity: expanded ? 1 : 0, display: expanded ? 'block' : 'none' }} />
            {expanded ? (
              isMenuOpen ? <ChevronUp size={15} strokeWidth={1.75} /> : <ChevronDown size={15} strokeWidth={1.75} />
            ) : (
              <Box sx={{ position: 'absolute', right: 4, display: 'flex' }}><ChevronRight size={14} strokeWidth={1.75} /></Box>
            )}
          </ListItemButton>
        </ListItem>

        {expanded && (
          <Collapse in={isMenuOpen} timeout="auto" unmountOnExit>
            <List disablePadding>
              {item.children.map((child, ci) => {
                const active = pathIsActive(pathname, child.path);
                return (
                  <ListItem key={ci} disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                      selected={active}
                      onClick={() => handleNavigation(child.path)}
                      sx={{ minHeight: 38, pl: 4.5, pr: 2 }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: 2, justifyContent: 'center', color: undefined }}>
                        {child.icon}
                      </ListItemIcon>
                      <ListItemText primary={child.title} primaryTypographyProps={{ fontSize: '0.82rem' }} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        )}

        {!expanded && flyoutMenu && flyoutMenu.title === item.title && (
          <Popper open anchorEl={flyoutMenu.anchorEl} placement="right-start" sx={{ zIndex: (t) => t.zIndex.drawer + 2 }}>
            <ClickAwayListener onClickAway={() => setFlyoutMenu(null)}>
              <Paper elevation={4} onMouseEnter={handleFlyoutPaperEnter} onMouseLeave={handleFlyoutLeave}
                sx={{ minWidth: 200, py: 1, ml: 1, bgcolor: 'background.paper' }}>
                <Typography variant="overline" sx={{ px: 2, color: 'text.secondary' }}>{item.title}</Typography>
                <List disablePadding>
                  {item.children.map((child, ci) => (
                    <ListItem key={ci} disablePadding>
                      <ListItemButton selected={pathIsActive(pathname, child.path)} onClick={() => handleNavigation(child.path)}
                        sx={{ '& .MuiListItemText-primary': { color: 'text.primary' } }}>
                        <ListItemIcon sx={{ minWidth: 34, color: 'text.secondary' }}>{child.icon}</ListItemIcon>
                        <ListItemText primary={child.title} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </ClickAwayListener>
          </Popper>
        )}
      </Box>
    );
  };

  const drawerContent = (
    <>
      {/* Markenkopf: die Wortmarke wohnt jetzt in der dunklen Leiste (V1) */}
      <Box sx={{ height: 64, flexShrink: 0, display: 'flex', alignItems: 'center', px: expanded ? 2.5 : 0, justifyContent: expanded ? 'flex-start' : 'center' }}>
        <Wordmark variant="sidebar" compact={!expanded} onClick={() => handleNavigation('/')} />
      </Box>
      <Box sx={{ overflowX: 'hidden', overflowY: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <List sx={{ flexGrow: 1 }}>
          {navigationItems.map((item, i) =>
            item.type === 'menu' ? renderMenu(item, i) : renderSingle(item, i)
          )}
        </List>
        <Box>
          <Divider sx={{ borderColor: (t) => t.palette.tokens.sidebarLine, mx: 2, mb: 1 }} />
          {expanded && (
            <Typography variant="overline" sx={{ px: 3, color: (t) => t.palette.tokens.sidebarMuted }}>Verwaltung</Typography>
          )}
          <List disablePadding sx={{ pb: 1 }}>
            {adminItems.map((item, i) => renderSingle(item, `admin-${i}`))}
          </List>
        </Box>
      </Box>
    </>
  );

  // AppBar sitzt neben der vollflächigen Leiste (Desktop), mobil volle Breite
  const sideW = open ? drawerWidth : railWidth;

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar — neben der Leiste, nicht mehr darüber */}
      <AppBar position="fixed" sx={{
        zIndex: (t) => t.zIndex.drawer + 1,
        borderRadius: 0,
        width: { xs: '100%', md: `calc(100% - ${sideW}px)` },
        ml: { xs: 0, md: `${sideW}px` },
        transition: (t) => t.transitions.create(['width', 'margin-left'], {
          easing: t.transitions.easing.sharp,
          duration: t.transitions.duration.enteringScreen,
        }),
      }}>
        <Toolbar>
          <IconButton color="inherit" onClick={handleDrawerToggle} edge="start" sx={{ mr: 2 }} aria-label="Menü umschalten">
            <MenuIcon />
          </IconButton>

          {/* Wortmarke mobil in der Topbar (dort ist die Leiste zu) */}
          {isMobile && <Wordmark onClick={() => navigate('/')} />}

          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', px: 2 }}>
            <GlobalSearch />
          </Box>

          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Tooltip title="Kin fragen">
                <IconButton onClick={() => setAssistantOpen(true)} aria-label="KI-Assistent Kin öffnen" sx={{ p: 0.5 }}>
                  <KinAvatar size={26} />
                </IconButton>
              </Tooltip>
              <ThemeModeToggle />
              <Tooltip title={user.email || ''}>
                <Avatar sx={{ bgcolor: avatarBg, color: avatarTextColor(avatarBg), width: 34, height: 34, fontSize: '0.875rem' }}>
                  {user.first_name?.[0]}{user.last_name?.[0]}
                </Avatar>
              </Tooltip>
              <Tooltip title="Abmelden">
                <IconButton onClick={onLogout} sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }} aria-label="Abmelden">
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Sidebar — mobil als Overlay, ab md fest */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', borderRadius: 0 } }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: open ? drawerWidth : railWidth,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            '& .MuiDrawer-paper': {
              width: open ? drawerWidth : railWidth,
              boxSizing: 'border-box',
              borderRadius: 0,
              overflowX: 'hidden',
              transition: (t) => t.transitions.create('width', {
                easing: t.transitions.easing.sharp,
                duration: t.transitions.duration.enteringScreen,
              }),
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Hauptinhalt */}
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, minWidth: 0, width: '100%' }}>
        <Toolbar />
        <React.Fragment key={dataTick}>{children}</React.Fragment>
      </Box>

      {/* KI-Assistent Kin (A1) — bleibt gemountet, damit das Gespräch beim
          Schließen und beim Seitenwechsel erhalten bleibt */}
      <AssistantPanel open={assistantOpen} onClose={() => setAssistantOpen(false)} />
    </Box>
  );
};

export default Layout;
